import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { supabase } from '@/lib/supabase';

// =========================================
// Stripe client (singleton)
// =========================================

let stripePromise: Promise<Stripe | null> | null = null;

export function getStripe(): Promise<Stripe | null> {
  if (!stripePromise) {
    const key = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
    if (!key) {
      console.warn('VITE_STRIPE_PUBLISHABLE_KEY is not set');
      return Promise.resolve(null);
    }
    stripePromise = loadStripe(key);
  }
  return stripePromise;
}

// =========================================
// Types
// =========================================

export interface CreditPackage {
  id: string;
  name: string;
  credits: number;
  price_eur: number;
  price_per_credit: number;
  stripe_price_id: string;
  popular: boolean;
  active: boolean;
}

export interface Transaction {
  id: string;
  user_id: string;
  type: 'purchase' | 'usage' | 'refund' | 'bonus';
  amount: number;
  balance_after: number;
  description: string | null;
  stripe_checkout_session_id: string | null;
  created_at: string;
}

export interface CheckoutResponse {
  checkout_url: string;
  session_id: string;
}

// =========================================
// API functions
// =========================================

/** Fetch all active credit packages */
export async function fetchCreditPackages(): Promise<CreditPackage[]> {
  const { data, error } = await supabase
    .from('credit_packages')
    .select('*')
    .eq('active', true)
    .order('credits', { ascending: true });

  if (error) throw error;
  return (data ?? []) as CreditPackage[];
}

/** Fetch user transaction history */
export async function fetchTransactions(userId: string): Promise<Transaction[]> {
  const { data, error } = await supabase
    .from('transactions')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(50);

  if (error) throw error;
  return (data ?? []) as Transaction[];
}

/** Create a Stripe Checkout session via Edge Function */
export async function createCheckoutSession(
  packageId: string,
  successUrl: string,
  cancelUrl: string
): Promise<CheckoutResponse> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;

  if (!token) throw new Error('Not authenticated');

  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-checkout`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({
        package_id: packageId,
        success_url: successUrl,
        cancel_url: cancelUrl,
      }),
    }
  );

  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.error ?? 'Failed to create checkout session');
  }

  return response.json();
}
